<template>
  <div class="plan-group">
    <div class="plan-group__title">
      {{ $t("message.payments.selectedPlan") }}
    </div>
    <div class="plan-list">
      <div
        v-for="(item, index) in paymentPlans"
        :key="item.product_id"
        class="plan-list__item "
        :class="{
          'is-disabled': overLimitUsage(item.limits).length
        }"
        @click="
          !overLimitUsage(item.limits).length ? selectItem(item, index) : ''
        "
      >
        <div
          v-if="overLimitUsage(item.limits).length"
          class="plan-list__disabled-card"
        >
          <v-icon size="14">mdi-lock</v-icon>
          <span>{{ $t("message.payments.overLimit") }}</span>
        </div>
        <div
          class="plan-list__card"
          :class="{ 'is-active': selectedPlan.product_id === item.product_id }"
        >
          <div class="plan-list__content">
            <div class="plan-list__title">
              {{ item.name }}
            </div>

            <button
              v-if="overLimitUsage(item.limits).length"
              class="plan-list__details-btn"
              @click="viewPlanLimits(item)"
            >
              <v-icon size="14">mdi-chart-bar</v-icon>
              <span>{{ $t("message.payments.viewUsageLimits") }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <ui-dialog
      v-if="dialogData"
      show
      max-width="1000px"
      @input="dialogData = null"
    >
      <template #header>
        <h3>{{ $t("message.payments.usageLimits") }}</h3>
      </template>
      <div class="content">
        <p>
          {{ limitDescription }}
        </p>
        <v-expansion-panels class="limit-list">
          <v-expansion-panel
            v-for="item in dialogData"
            :key="item.key"
            class="panel"
          >
            <v-expansion-panel-header
              class="panel__header"
              :hide-actions="!showLimitsManagement"
            >
              <div class="limit-list__item">
                <div class="limit-list__label">
                  <div>{{ $t(`message.payments.limits.${item.key}`) }}</div>
                  <div class="limit-list__secondary-label">
                    +{{ item.currentUsage - item.maximumLimit }}
                    {{ $t("message.payments.aboveLimit") }}
                  </div>
                </div>

                <div class="limit-bar">
                  <div
                    class="limit-bar__over-limit"
                    :style="{
                      width: `${getOverLimitPercentage(
                        item.currentUsage,
                        item.maximumLimit
                      )}%`
                    }"
                  ></div>
                </div>
                <div class="limit-legend">
                  <div>
                    {{ $t("message.payments.limit") }}: {{ item.maximumLimit }}
                  </div>
                  <div class="limit-legend__over-limit">
                    {{ $t("message.payments.currently") }}:
                    {{ item.currentUsage }}
                  </div>
                </div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content v-if="showLimitsManagement">
              <component
                :is="getComponent(item.key)"
                @deleted="fetchLimits"
              ></component>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
      <template #actions>
        <div class="w-100 d-flex justify-end">
          <ui-button color="primary" @click="dialogData = null">
            {{ $t("message.common.ok") }}
          </ui-button>
        </div>
      </template>
    </ui-dialog>
  </div>
</template>

<script>
import AddressesLimitManagement from "@/components/LimitsManagement/AddressesLimitManagement.vue";
import TagsLimitManagement from "@/components/LimitsManagement/TagsLimitManagement.vue";
import UsersLimitManagement from "@/components/LimitsManagement/UsersLimitManagement.vue";
import CompetitorsLimitManagement from "@/components/LimitsManagement/CompetitorsLimitManagement.vue";
import KeywordsLimitManagement from "@/components/LimitsManagement/KeywordsLimitManagement.vue";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";

export default {
  name: "PlanSelector",
  components: {
    AddressesLimitManagement,
    TagsLimitManagement,
    UsersLimitManagement,
    CompetitorsLimitManagement,
    KeywordsLimitManagement
  },
  data() {
    return {
      plan: null,
      planItems: [],
      isLoading: false,
      dialogData: null,
      targetPlanLimits: null,
      panels: [
        {
          component: AddressesLimitManagement,
          limit: "max_facilities"
        },
        {
          component: TagsLimitManagement,
          limit: "max_ai_categorization_categories"
        },
        {
          component: UsersLimitManagement,
          limit: "max_company_users"
        },
        {
          component: CompetitorsLimitManagement,
          limit: "max_listings_competitors"
        },
        {
          component: KeywordsLimitManagement,
          limit: "max_maps_keywords"
        }
      ]
    };
  },
  computed: {
    initialPlanIndex() {
      return this.$store.state.payments.initialPlanIndex;
    },
    overLimitUsage() {
      return this.$store.getters["payments/overLimitUsage"];
    },
    paymentPlans() {
      return this.$store.getters["payments/paymentPlans"];
    },
    selectedPlan() {
      return this.$store.state.payments.selectedPlan;
    },
    showLimitsManagement() {
      return isFeatureFlagEnabled(FEATURE_FLAG.CN_6363_LIMITS_DELETION);
    },
    limitDescription() {
      if (this.showLimitsManagement) {
        return this.$t("message.payments.limitDescription");
      }
      return this.$t("message.payments.limitDescriptionNoDeletion");
    }
  },

  methods: {
    getOverLimitPercentage(currentUsage, limit) {
      let fullWidth;
      if (limit === 0) {
        return 100;
      } else {
        fullWidth = currentUsage;
      }

      return (((currentUsage - limit) / fullWidth) * 100).toFixed(2);
    },
    getPlanChangeType(index) {
      if (index < this.initialPlanIndex) {
        return "downgrade";
      } else if (index > this.initialPlanIndex) {
        return "upgrade";
      }
      return "no_change";
    },
    selectItem(item, index) {
      const planChangeType = this.getPlanChangeType(index);
      this.$store.dispatch("payments/setPlanChangeType", planChangeType);
      this.$store.dispatch("payments/setSelectedPlan", item);
    },
    handleEditPlan() {
      this.$emit("change");
    },
    viewPlanLimits(item) {
      this.dialogData = this.overLimitUsage(item.limits);
      this.targetPlanLimits = item.limits;
    },
    async fetchLimits() {
      await this.$store.dispatch("payments/getPlans");
      const usage = this.overLimitUsage(this.targetPlanLimits);
      if (!usage.length) {
        this.dialogData = null;

        return;
      }
      this.dialogData = usage;
    },
    getComponent(key) {
      const panel = this.panels.find(panel => panel.limit == key);
      if (!panel) {
        return "div";
      }
      return panel.component;
    }
  }
};
</script>

<style lang="scss" scoped>
.v-expansion-panel {
  &::before {
    box-shadow: none;
  }

  &:not(:first-child)::after {
    border: none;
  }

  &-content {
    position: relative;
  }
}

.panel {
  &:not(:last-child) {
    border-bottom: 1px solid $light-3;
  }

  &__header {
    display: flex;
    gap: $spacing * 4;
    padding: $spacing * 2 0;
  }
}

.limit-list {
  display: flex;
  flex-direction: column;
  gap: $spacing * 1.5;
  &__item {
    display: flex;
    flex-direction: column;
    gap: $spacing * 0.75;
  }
  &__label {
    color: $grey-darker;
    font-weight: 500;
    display: flex;
    justify-content: space-between;
    margin-bottom: $spacing * 0.2;
  }
  &__secondary-label {
    color: $red-1;
    font-size: 12px;
  }
}
.limit-legend {
  display: flex;
  justify-content: space-between;
  @include font-size(12px);
  color: $grey-darker;
  margin-top: $spacing * 0.15;

  & > * {
    line-height: 1em;
  }

  &__over-limit {
    color: $red-1;
  }
}
.limit-bar {
  height: 5px;
  display: flex;
  width: 100%;
  background: $gray-200;
  justify-content: flex-end;

  &__over-limit {
    background: $red-1;
    height: 100%;
    position: relative;
    border-radius: 0 8px 8px 0;

    &:before {
      content: "";
      width: 12px;
      height: 12px;
      background: white;
      position: absolute;
      border-radius: 100%;
      bottom: -3.5px;
      left: -6px;
      border: 4px solid $red-1;
    }
  }
}
.plan-group {
  display: flex;
  flex-direction: column;
  gap: $spacing;

  &__title {
    line-height: 1em;
    @include font-size(14px);
  }
}
.plan-list {
  display: flex;
  gap: $spacing * 2;
  flex-wrap: wrap;
  &__disabled-card {
    background: $gray-200;
    color: gray;
    position: relative;
    z-index: 0;
    @include font-size(10px);
    text-align: center;
    border-radius: 8px 8px 0 0;
    padding: $spacing * 0.6 0 $spacing * 1 0;
    display: flex;
    justify-content: center;
    gap: $spacing * 0.25;
    font-weight: 600;
    span {
      letter-spacing: 0.1px;
    }

    ::v-deep {
      .v-icon {
        opacity: 0.5;
      }
    }
  }

  &__details-btn {
    background: $gray-200;
    color: $grey-darker;
    @include font-size(10px);
    border-radius: 4px;
    padding: $spacing * 0.8 $spacing $spacing * 0.8 $spacing;
    margin-top: $spacing * 2;
    align-self: center;
    line-height: 1em;
    display: flex;
    align-items: center;
    font-weight: 600;
    transition: background 0.2s;
    gap: $spacing * 0.5;

    span {
      white-space: nowrap;
    }

    ::v-deep {
      .v-icon {
        color: $grey-darker;
      }
    }

    &:hover {
      background: $gray-300;
    }
  }

  &__item {
    width: calc((100% / 4) - #{$spacing * 1.5});
    flex-shrink: 0;
    min-height: 100px;
    height: auto;

    &.is-disabled {
      height: 100%;
      cursor: not-allowed;

      .plan-list__card {
        border-color: $gray-200;
        margin-top: $spacing * -0.75;
        cursor: not-allowed;

        &:hover {
          border-color: $gray-200;
          background: white;
        }
      }
      .plan-list__title {
        margin-top: $spacing * 1.25;
        color: gray;
      }
    }
  }
  &__card {
    z-index: 1;
    cursor: pointer;
    display: flex;
    background: white;
    border: 1px solid $gray-300;
    padding: $spacing * 2 $spacing * 1.5;
    border-radius: 8px;
    align-items: center;
    transition-property: border-color, background;
    transition-duration: 0.2s;
    flex-grow: 1;
    text-align: center;
    height: 100%;

    background-color: white;
    position: relative;

    &:hover,
    &.is-active {
      border-color: $primary;
      background: $gray-500;
    }

    &.is-active {
      position: relative;
      .plan-list__title {
        color: $primary;
      }

      &:after {
        font: normal normal bold 13px/1.25 "Material Design Icons";
        position: absolute;
        text-align: center;
        right: -8px;
        top: -8px;
        border-radius: 100%;
        content: "\F12C";
        color: white;
        width: 16px;
        height: 16px;
        background: $primary;
        animation: showCheckbox 0.3s;
      }
    }
  }

  &__title {
    @include font-size(16px);
    font-weight: 600;
    line-height: 1em;
  }

  &__content {
    gap: $spacing * 0.5;
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  &__action {
    cursor: pointer;
  }
}

@keyframes showCheckbox {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}
.content > p {
  color: $grey-darker;
  @include font-size(14px);
}
</style>
